import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Palette, Loader2, Check, Sun, Moon, Sparkles, SlidersHorizontal, Crosshair } from 'lucide-react';
import { motion } from 'motion/react';
import { http } from '@/network/http';
import { useSettings } from '@/hooks/useSettings';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/glass';
import {
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';
import {
  SECTION_IDS,
  SECTION_LABELS,
  SECTION_PROPS,
  PROP_LABELS,
  SCHEME_PRESETS,
  schemeKey,
  colorKey,
} from '../utils/colorSchemes';
import type { ColorScheme, SectionId } from '../utils/colorSchemes';

const SCHEMES: { id: ColorScheme; icon: typeof Sun; labelEn: string; labelHe: string }[] = [
  { id: 'light', icon: Sun, labelEn: 'Light', labelHe: 'בהיר' },
  { id: 'dark', icon: Moon, labelEn: 'Dark', labelHe: 'כהה' },
  { id: 'glass', icon: Sparkles, labelEn: 'Glass', labelHe: 'זכוכית' },
  { id: 'hk', icon: Crosshair, labelEn: 'HK', labelHe: 'HK' },
  { id: 'custom', icon: SlidersHorizontal, labelEn: 'Custom', labelHe: 'מותאם' },
];

export function ColorManagerPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useSettings();
  const isHebrew = i18n.language === 'he';

  // Local state: scheme per section + custom colors
  const [schemes, setSchemes] = useState<Record<SectionId, ColorScheme>>({
    userNav: 'hk',
    userBnav: 'hk',
    userPage: 'hk',
    adminNav: 'hk',
    adminPage: 'hk',
    authPage: 'hk',
  });

  const [customColors, setCustomColors] = useState<Record<string, string>>({});

  // Sync from server settings
  useEffect(() => {
    if (!settings) return;
    const newSchemes = { ...schemes };
    for (const section of SECTION_IDS) {
      const val = settings[schemeKey(section)];
      if (val && ['light', 'dark', 'glass', 'hk', 'custom'].includes(val)) {
        newSchemes[section] = val as ColorScheme;
      }
    }
    setSchemes(newSchemes);

    const newCustom: Record<string, string> = {};
    for (const section of SECTION_IDS) {
      for (const prop of SECTION_PROPS[section]) {
        const key = colorKey(section, prop);
        if (settings[key]) {
          newCustom[key] = settings[key];
        }
      }
    }
    setCustomColors(newCustom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const saveSettings = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      await http.put('/settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success(t('settings.saved'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleSave = () => {
    const payload: Record<string, string> = {};
    for (const section of SECTION_IDS) {
      payload[schemeKey(section)] = schemes[section];
      // Always persist custom colors so switching to custom restores them
      for (const prop of SECTION_PROPS[section]) {
        const key = colorKey(section, prop);
        const fallback = (SCHEME_PRESETS.light[section] as unknown as Record<string, string>)[prop];
        payload[key] = customColors[key] || fallback;
      }
    }
    saveSettings.mutate(payload);
  };

  const setScheme = (section: SectionId, scheme: ColorScheme) => {
    setSchemes((prev) => ({ ...prev, [section]: scheme }));
  };

  const setCustomColor = (section: SectionId, prop: string, value: string) => {
    const key = colorKey(section, prop);
    setCustomColors((prev) => ({ ...prev, [key]: value }));
  };

  /** Get the resolved color for a section.prop (based on current local state) */
  const getResolvedColor = (section: SectionId, prop: string): string => {
    const scheme = schemes[section];
    if (scheme !== 'custom') {
      return (SCHEME_PRESETS[scheme][section] as unknown as Record<string, string>)[prop];
    }
    const key = colorKey(section, prop);
    return customColors[key] || (SCHEME_PRESETS.light[section] as unknown as Record<string, string>)[prop];
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="rounded-xl border border-gray-200 p-8 shadow-sm glass-surface">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* Header */}
      <motion.div variants={staggerItem} className="space-y-4">
        <div className="inline-flex items-center justify-center rounded-xl border border-gray-200 p-3 shadow-sm glass-surface">
          <Palette className="h-6 w-6 text-slate-800" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">
            {isHebrew ? 'צבעי ממשק' : 'UI Colors'}
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            {isHebrew
              ? 'הגדר ערכת צבעים לכל חלק בממשק בנפרד.'
              : 'Configure color schemes for each UI section independently.'}
          </p>
        </div>
      </motion.div>

      {/* Section cards */}
      {SECTION_IDS.map((section) => (
        <motion.div key={section} variants={staggerItem}>
          <GlassCard
            title={isHebrew ? SECTION_LABELS[section].he : SECTION_LABELS[section].en}
            icon={<Palette className="h-4 w-4" />}
          >
            {/* Scheme selector */}
            <div className="flex flex-wrap gap-2 mb-4">
              {SCHEMES.map((s) => {
                const isSelected = schemes[section] === s.id;
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => setScheme(section, s.id)}
                    className="relative flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all"
                    style={{
                      border: isSelected
                        ? '2px solid var(--color-primary-500)'
                        : '2px solid rgba(0,0,0,0.08)',
                      backgroundColor: isSelected
                        ? 'var(--color-primary-50)'
                        : 'rgba(255,255,255,0.6)',
                      color: isSelected
                        ? 'var(--color-primary-700)'
                        : 'var(--color-label-secondary)',
                    }}
                  >
                    <Icon size={16} />
                    {isHebrew ? s.labelHe : s.labelEn}
                    {isSelected && (
                      <Check size={14} className="text-primary-600" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Color preview swatches */}
            <div className="flex flex-wrap gap-2 mb-4">
              {SECTION_PROPS[section].map((prop) => {
                const color = getResolvedColor(section, prop);
                const label = isHebrew ? PROP_LABELS[prop]?.he : PROP_LABELS[prop]?.en;
                return (
                  <div key={prop} className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 rounded-lg border border-gray-300"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs text-neutral-500">{label || prop}</span>
                  </div>
                );
              })}
            </div>

            {/* Custom color pickers (only when scheme=custom) */}
            {schemes[section] === 'custom' && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-t border-gray-200 pt-4">
                {SECTION_PROPS[section].map((prop) => {
                  const key = colorKey(section, prop);
                  const value = customColors[key] || (SCHEME_PRESETS.light[section] as unknown as Record<string, string>)[prop];
                  const label = isHebrew ? PROP_LABELS[prop]?.he : PROP_LABELS[prop]?.en;
                  return (
                    <div key={prop}>
                      <label className="mb-1 block text-xs font-semibold text-neutral-600">
                        {label || prop}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={toHex(value)}
                          onChange={(e) => setCustomColor(section, prop, e.target.value)}
                          className="h-8 w-8 cursor-pointer rounded-lg border border-gray-200"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setCustomColor(section, prop, e.target.value)}
                          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-mono shadow-sm focus:outline-none"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </motion.div>
      ))}

      {/* Save */}
      <motion.div variants={staggerItem} className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saveSettings.isPending}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-primary-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-700 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveSettings.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('common.save')}
        </button>
      </motion.div>
    </motion.div>
  );
}

/** Convert rgba/named colors to hex for <input type="color"> (best effort) */
function toHex(color: string): string {
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) return color;
  // For rgba/non-hex, return a fallback — the text input still shows the real value
  return '#000000';
}
